import { NgTemplateOutlet } from '@angular/common';
import { Component, DOCUMENT, ElementRef, inject, input, output, viewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { apiErrorMessage } from '../../../helpers';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { firstValueFrom } from 'rxjs';

import { DragAndDropDocument, EmailRefusal, ReportTemplate } from '../../../models';
import {
  DocumentPipelineService,
  DocumentViewerService,
  DragAndDropService,
  EmailService,
  FileService,
  ModalService,
  NotificationService,
  ReportAuditService,
} from '../../../services';
import { BaseComponent } from '../../base/base.component';
import { EmailModalComponent } from '../../email-modal/email-modal.component';
import { EmailRefusalCodes, ReportStatuses } from '../../../constants';
import { ignoreModalConfirm, ignoreModalError } from '../../../helpers';
import { OkModalComponent } from '../../modals/ok-modal/ok-modal.component';
import { RepeatClickButtonComponent } from '../../repeat-click-button/repeat-click-button.component';

/**
 * One toolbar strip above a full-width report: the report selector, the PDF
 * viewer's page/zoom controls (published by DocumentPreviewComponent via
 * DocumentViewerService — only present while a PDF preview is on screen), and
 * the Email/Download/Print/Upload actions. The viewer's own toolbar is hidden
 * so every action goes through these buttons and the report audit.
 */
@Component({
  selector: 'app-report-toolbar',
  templateUrl: './report-toolbar.component.html',
  styleUrl: './report-toolbar.component.scss',
  imports: [FormsModule, NgSelectModule, NgTemplateOutlet, RepeatClickButtonComponent],
})
export class ReportToolbarComponent extends BaseComponent {
  private readonly document = inject<Document>(DOCUMENT);
  private readonly modalService = inject(ModalService);
  private readonly emailService = inject(EmailService);
  private readonly reportAuditService = inject(ReportAuditService);
  private readonly notificationService = inject(NotificationService);
  private readonly fileService = inject(FileService);
  private readonly dragAndDropService = inject(DragAndDropService);
  private readonly documentPipelineService = inject(DocumentPipelineService);

  private readonly reportContent = viewChild<ElementRef<HTMLDivElement>>('reportContent');

  public readonly upload = input(false);
  public readonly folderName = input('');
  public readonly subFolderName = input('');
  public readonly container = input('');

  // Opt-in: print a SELF-CONTAINED copy of the report in a new window instead of the live
  // page. Needed by any report whose own stylesheet decides the page - the weaving ticket
  // is landscape, and the in-app copy cannot carry that.
  //
  // ⚠️ Why the live page cannot: the report reaches the DOM through [innerHTML], which
  // strips <style> blocks, and @page cannot be scoped to a selector - so a landscape rule
  // would have to be global and would then apply to every other report too.
  //
  // The host supplies the already-wrapped HTML (the API's `wrap=true`); default false, so
  // no existing report changes behaviour.
  public readonly printWrapped = input(false);
  public readonly wrappedHtml = input<string>();

  // Opt-in: build the Download / Email / Upload PDF on a LANDSCAPE page. Those all go
  // through jsPDF, which is given its page size in code - the report's own @page rule never
  // reaches it - so a landscape report downloads portrait unless the host says otherwise.
  // Default false, so no existing report changes.
  public readonly landscape = input(false);
  public readonly emailed = output();
  // Carries the document that went to the printer, so a screen offering several in its dropdown
  // can react to one of them only (the Job screen stamps the job printed for the weaving ticket
  // and not for the yarn picking list).
  public readonly printed = output<ReportTemplate>();
  public readonly downloaded = output();

  protected readonly viewer = inject(DocumentViewerService).viewer;

  private reports: ReportTemplate[] = [];
  protected reportNames: string[] = [];
  protected selectedReportName: string;
  protected selectedReport: ReportTemplate;

  private uploadDetails: DragAndDropDocument = {
    folderName: undefined,
    subFolderName: undefined,
    fileName: undefined,
    container: undefined,
  };

  setReports(reports: ReportTemplate[], selectFirst = false) {
    const previouslySelected = this.selectedReport?.report;

    this.reports = reports;
    this.reportNames = reports.map((x) => x.report);

    this.selectedReport = reports.find((x) => x.report === this.selectedReportName);
    if (!this.selectedReport) {
      this.selectedReportName = undefined;
    }

    // A screen calls setReports again when the record behind the documents has changed —
    // every Reports tab re-reads on its feature service's refreshDetails. The PDF preview
    // loads on a CHANGE of documentType/reportId, and an amend changes neither, so nothing
    // would re-fetch and the operator would keep looking at the pre-amend document (a
    // delivery charge added to an order did not appear, 2026-08-10). The legacy HTML reports
    // re-fetched their own markup, which is why this only shows up on the pipeline.
    //
    // Only when the SAME report stays selected: a different selection re-stamps the template
    // and the new preview loads itself, so reloading here would refetch the outgoing one.
    //
    // This MUST come before the auto-select early return below. It used to sit after it, where
    // it could never run: a document being open means selectedReport is truthy, which is
    // exactly the condition that returns. That is why the 2026-08-10 fix appeared not to reach
    // the other tabs - it reached none of them.
    if (previouslySelected && previouslySelected === this.selectedReport?.report) {
      this.viewer()?.reload();
    }

    // One document means there is nothing to choose: picking it out of a dropdown of one is
    // a step that only ever has one answer. With several the placeholder stays - choosing
    // for the user would render a document they had not asked for.
    if (this.selectedReport || !(reports.length === 1 || (selectFirst && reports.length > 0))) {
      return;
    }

    // A tick later, deliberately. Every caller runs setReports from ngAfterViewInit, so
    // selecting here would change bound state inside the pass that has just checked it -
    // NG0100. Next tick it is an ordinary update.
    setTimeout(() => {
      this.selectedReportName = reports[0].report;
      this.onReportChange();
    });
  }

  protected onReportChange() {
    this.selectedReport = this.reports.find((x) => x.report === this.selectedReportName);
  }

  /** Page input guard: ignore empty/out-of-range values while the user types. */
  protected setViewerPage(page: number) {
    this.viewer()?.setPage(page);
  }

  protected onEmail() {
    this.email(this.selectedReport.suffix, this.selectedReport.sellingCompany, this.selectedReport.report);
  }

  protected onDownload() {
    this.download(this.selectedReport.suffix);
  }

  protected onUpload() {
    this.uploadToBlob(this.selectedReport.suffix);
  }

  private async email(suffix?: string, sellingCompany?: string, emailSubject?: string) {
    // The PDF is generated BEFORE the modal opens, so the compose window can show the one
    // attachment that is certain to go — named and sized — and the file shown is the file
    // sent. (The old flow generated it after composing, and the document appeared nowhere.)
    let pdfForm: FormData;
    try {
      pdfForm = await this.getReportPdfFormData(suffix);
    } catch (err: unknown) {
      this.notificationService.fail(
        await this.documentPipelineService.getErrorMessage(
          err,
          $localize`Failed to send ${this.selectedReportName} email`,
        ),
      );
      return;
    }
    const pdfBlob = pdfForm.get('pdf') as Blob;

    // openLarge = Bootstrap's modal-xl (~1140px) — the compose window from the mockup.
    // ⚠️ NOT openXL, which in this codebase maps to modal-full (screen-wide): a compose
    // window the width of the screen strands the fields miles from their labels.
    const { modal, component } = this.modalService.openLarge<EmailModalComponent>(EmailModalComponent);
    component.reportTemplate.set({
      ...this.selectedReport,
      // A batch (and any report without a document number) has no suffix — don't
      // put a bare "-undefined" in the subject line.
      emailSubject: suffix ? `${emailSubject}-${suffix}` : emailSubject,
      emailBody: this.selectedReport.emailBody ?? '',
      suffix: suffix,
    });
    // So the modal can list the files configured against this Document. The same pair is sent to
    // the API below, which is what actually attaches them - this is only the preview.
    component.documentType.set(this.selectedReport?.documentType);
    component.reportId.set(this.selectedReport?.reportId);
    component.documentPdfName.set(`${this.reportFileName(suffix)}.pdf`);
    component.documentPdfSize.set(pdfBlob?.size);
    modal.result.then((model: ReportTemplate) => {
      // The modal closes with the composed value: recipients as comma-separated strings
      // (contacts picked via Contacts… are already chips on Cc), the body in the narrative
      // storage contract.
      this.emailService
        .sendReport(
          {
            email: model.email,
            ccEmail: model.ccEmail,
            bccEmail: model.bccEmail,
            subject: model.emailSubject,
            emailBody: model.emailBody,
            attachDocuments: model.attachDocuments ?? false,
            type: model.type,
            key: model.suffix,
            sellingCompany,
            documentType: this.selectedReport?.documentType,
            reportId: this.selectedReport?.reportId,
          },
          pdfForm,
        )
        .pipe(this.untilDestroyed())
        .subscribe({
          next: () => {
            this.notificationService.emailSuccess(this.selectedReportName);
            this.reportAuditService
              .updateReportAuditDetails(this.selectedReport.type, this.selectedReport.suffix, ReportStatuses.emailed)
              .pipe(this.untilDestroyed())
              .subscribe({
                next: () => this.emailed.emit(),
                error: (error: HttpErrorResponse) => {
                  const { modal, component } = this.modalService.openMedium<OkModalComponent>(OkModalComponent);
                  component.header.set($localize`Create Contact`);
                  component.message.set(apiErrorMessage(error, $localize`The report could not be produced`));
                  modal.result.then(ignoreModalConfirm, ignoreModalError);
                },
              });
          },
          error: (error: HttpErrorResponse) => this.emailRefused(error),
        });
    }, ignoreModalError);
  }

  /**
   * A configured attachment that could not be read stops the send (4Spaces, Oct 2026, D-1). The API
   * says so with a code and the file names; the sentence is worded here so it is in the user's
   * language (the email controller has no translation filter). Anything else keeps the old toast.
   */
  private emailRefused(error: HttpErrorResponse) {
    const refusal = this.emailRefusal(error);
    if (refusal?.code !== EmailRefusalCodes.attachmentUnreadable) {
      this.notificationService.emailFail(this.selectedReportName);
      return;
    }

    // File names are data, shown as stored; the document is the Documents screen's own name.
    const files = (refusal.files ?? []).join(', ');
    const document = refusal.document ?? this.selectedReport?.documentType ?? '';
    const { modal, component } = this.modalService.openMedium<OkModalComponent>(OkModalComponent);
    component.header.set($localize`Email Not Sent`);
    component.message.set(
      $localize`The email was not sent. These attachments could not be read: ${files}:files:. Upload them again on Documents → ${document}:document: → Attachments, or send the document another way.`,
    );
    modal.result.then(ignoreModalConfirm, ignoreModalError);
  }

  // The send posts for TEXT, so a 400's JSON body arrives as a string.
  private emailRefusal(error: HttpErrorResponse): EmailRefusal | undefined {
    const body = error?.error;
    if (body && typeof body === 'object') {
      return body as EmailRefusal;
    }

    try {
      return typeof body === 'string' ? (JSON.parse(body) as EmailRefusal) : undefined;
    } catch {
      return undefined;
    }
  }

  private async download(suffix?: string) {
    if (this.useDocumentPipeline) {
      this.documentPdf(this.reportFileName(suffix))
        .pipe(this.untilDestroyed())
        .subscribe({
          next: (blob: Blob) => {
            this.documentPipelineService.saveBlob(blob, `${this.reportFileName(suffix)}.pdf`);
            this.audit(ReportStatuses.downloaded, () => this.downloaded.emit());
          },
          error: async (err: unknown) =>
            this.notificationService.fail(
              await this.documentPipelineService.getErrorMessage(
                err,
                $localize`Failed to download ${this.selectedReportName}`,
              ),
            ),
        });
      return;
    }

    this.audit(ReportStatuses.downloaded, () => this.downloaded.emit());
    // landscape() must be threaded through - jsPDF takes its page size in code, and the
    // declared-but-unpassed input was why a landscape ticket downloaded portrait
    // (Steve, 2026-09-10).
    await this.fileService.downloadPdf(
      this.reportContent().nativeElement,
      this.selectedReportName,
      suffix,
      this.landscape(),
    );
  }

  protected print() {
    const report = this.selectedReport;

    if (this.useDocumentPipeline) {
      // Download (not Preview): the server marks documents as printed only on
      // Download/Print actions — Preview must NEVER trigger status writes.
      this.documentPdf(this.reportFileName(this.selectedReport.suffix))
        .pipe(this.untilDestroyed())
        .subscribe({
          next: (blob: Blob) => {
            this.documentPipelineService.printBlob(blob);
            this.audit(ReportStatuses.printed, () => this.printed.emit(report));
          },
          error: async (err: unknown) =>
            this.notificationService.fail(
              await this.documentPipelineService.getErrorMessage(
                err,
                $localize`Failed to print ${this.selectedReportName}`,
              ),
            ),
        });
      return;
    }

    this.audit(ReportStatuses.printed, () => this.printed.emit(report));
    const title = document.title;
    window.print();
    this.document.title = title;
  }

  // Prints the wrapped HTML on its own, so the report's stylesheet - page size, margins,
  // its own fonts - is what decides the printed page.
  private printSelfContained() {
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      // Pop-ups blocked. Say so rather than doing nothing, which reads as a broken button.
      const { modal, component } = this.modalService.openSmall<OkModalComponent>(OkModalComponent);
      component.header.set($localize`Print`);
      component.message.set($localize`Allow pop-ups for this site to print this report.`);
      modal.result.then(ignoreModalConfirm, ignoreModalError);
      return;
    }

    printWindow.document.write(this.wrappedHtml());
    printWindow.document.close();

    // Wait for the document - fonts and the barcode face especially - before printing, or
    // the dialog opens over a half-laid-out page.
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  }

  private async uploadToBlob(suffix?: string) {
    let pdf: FormData;
    try {
      pdf = await this.getReportPdfFormData(suffix);
    } catch (err: unknown) {
      this.notificationService.fail(
        await this.documentPipelineService.getErrorMessage(
          err,
          $localize`Failed to upload ${this.selectedReportName} file`,
        ),
      );
      return;
    }

    this.uploadDetails.folderName = this.folderName();
    this.uploadDetails.subFolderName = this.subFolderName();
    this.uploadDetails.fileName = `${this.selectedReportName}${suffix ? '-' : ''}${suffix}.pdf`;
    this.uploadDetails.container = this.container();

    this.dragAndDropService.uploadPDF(this.uploadDetails, pdf).subscribe({
      next: () => this.notificationService.uploadSuccess(this.selectedReportName),
      error: () => this.notificationService.uploadFail(this.selectedReportName),
    });
  }

  /**
   * True when the selected report has opted into the unified document pipeline
   * (server-rendered PDF). See ReportTemplate.documentType/reportId. When false,
   * the legacy client-side path (jsPDF / window.print of the on-screen HTML) is used.
   */
  private get useDocumentPipeline(): boolean {
    return !!(this.selectedReport?.documentType && (this.selectedReport?.reportId || this.isBulkReport));
  }

  /** True when the selected report is a BATCH of records combined into one PDF. */
  private get isBulkReport(): boolean {
    return (this.selectedReport?.reportIds?.length ?? 0) > 0;
  }

  /**
   * The server-rendered PDF behind Download, Print and Email — the SAME artifact for
   * all three, and the same call whether the report is a single record or a batch (the
   * pipeline takes reportIds alongside reportId and renders once either way). One helper
   * so the three actions cannot drift apart.
   */
  private documentPdf(fileName: string) {
    return this.isBulkReport
      ? this.documentPipelineService.downloadBatch({
          documentType: this.selectedReport.documentType,
          reportIds: this.selectedReport.reportIds,
          title: fileName,
        })
      : this.documentPipelineService.download(this.selectedReport.documentType, this.selectedReport.reportId, fileName);
  }

  private reportFileName(suffix?: string): string {
    return `${this.selectedReportName}${suffix ? '-' : ''}${suffix ?? ''}`;
  }

  /** PDF for the Email/Upload flows: the server-rendered document when piloted, otherwise jsPDF. */
  private async getReportPdfFormData(suffix?: string): Promise<FormData> {
    if (this.useDocumentPipeline) {
      const blob = await firstValueFrom(this.documentPdf(this.reportFileName(suffix)));
      return this.documentPipelineService.toFormData(blob, this.reportFileName(suffix));
    }
    // Same threading as download() - the Email/Upload PDF must honour landscape() too.
    return this.fileService.getPdfOutput(
      this.reportContent().nativeElement,
      this.selectedReportName,
      suffix,
      this.landscape(),
    );
  }

  private audit(status: string, done: () => void) {
    // A batch has no single record to stamp — the audit is keyed on one document's
    // type + number. A batch is audited only when its opener supplies batchAudit (Sales
    // Invoices bulk print, which records every invoice in one call); Despatch Notes does not.
    if (this.isBulkReport) {
      const batchAudit = this.selectedReport.batchAudit;
      if (!batchAudit) {
        done();
        return;
      }

      batchAudit(status)
        .pipe(this.untilDestroyed())
        .subscribe({
          next: () => done(),
          error: (error: HttpErrorResponse) =>
            this.notificationService.fail(
              apiErrorMessage(error, $localize`The ${this.selectedReportName} batch could not be recorded`),
            ),
        });
      return;
    }

    this.reportAuditService
      .updateReportAuditDetails(this.selectedReport.type, this.selectedReport.suffix, status)
      .pipe(this.untilDestroyed())
      .subscribe(() => done());
  }
}
